/**
 * The Engagement create / edit dialog (f7.1).
 *
 * Built through f2.2's `Modal` on the prototype's own `modal-scrim` /
 * `modal-head` / `modal-body` / `modal-foot` shapes.
 *
 * **The create form carries the required five plus status, and nothing else.**
 * The brief rules that the secondary attributes *"belong on a details panel,
 * not in the primary create form"*, so they appear on EDIT only -- which is
 * also what makes adr-16's demonstration work: *"phases 4 to 7 are
 * demonstrated by creating a group, unit, client and engagement by hand"*, and
 * an engagement created with no secondary attribute set is valid.
 *
 * **Deliberate departures from the prototype**, each argued rather than
 * silent:
 *
 * | Element | What is built | Why |
 * | --- | --- | --- |
 * | A **required** `Project Head` select | **Absent** | Ownership is a `DeliveryRoleAssignment` with its own lifecycle, its own audit entries and its own history. A required create-form field would reintroduce the `project_head_id` column this feature refuses, and on a fresh installation there may be no assignable account at all. A Project Head is named from the Ownership card, exactly as a DEM, a CEM, a CM and a DM are |
 * | A **required** `Currency` select | **Absent** | Every Business Unit is single-currency -- `CU US DB (EUR)` is the only EUR one -- so the attribute is 100% determined two levels up. f5.1 owns it |
 * | `Planned end date` on CREATE | On EDIT only | Populated on **1 of 70** legacy rows. The prototype's three-date create form presents a field nobody fills |
 * | `PQR rating` offering `Green / Amber / Red` | A free-text field | The legacy writes a single letter, `G`, on 20 of 70 rows. The prototype's vocabulary is illustrative |
 * | `Delivery country` offering `United States` | f2.2's seeded countries | All six legacy values resolve against a seeded `country.name`, and the legacy writes `USA`. The seeded name wins |
 * | A `Client` select | Fixed by context | The tab is already inside one client, and an engagement moves between clients by being deactivated and re-created -- so the audit log shows the two events it actually is |
 *
 * **No control holds a map from code to label.** The status and delivery-stage
 * selects are populated from `GET /lookups` and the countries from
 * `GET /countries`, so a value renamed on the Settings screen renames itself
 * here.
 */

import { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

import type { Client, Engagement, EngagementUpdate } from '@/api/client';
import {
  useAddEngagement,
  useCountries,
  useLookupCatalog,
  useUpdateEngagement,
} from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { generalMessage, problemFor } from './engagementView';

/** The lookup types this dialog's two selects read. Values are the server's. */
const ENTITY_STATUS_TYPE = 'ENTITY_STATUS';
const DELIVERY_STAGE_TYPE = 'DELIVERY_STAGE';

/** Every field the server names in a refusal, so none is reported twice. */
const FIELD_KEYS = [
  'client_id',
  'code',
  'name',
  'start_date',
  'end_date',
  'planned_end_date',
  'status_id',
  'delivery_stage_id',
  'delivery_country_id',
  'product_type',
  'services',
  'methodology',
  'pal',
  'risk_level',
  'pqr_rating',
  'pqr_result',
  'portal_name',
  'portal_url',
  'dashboard_url',
  'delivery_program',
];

/** A sent empty string means "clear this attribute", which the server accepts. */
function orNull(value: string): string | null {
  return value.trim() === '' ? null : value.trim();
}

export function EngagementDialog({
  client,
  engagement,
  onClose,
}: {
  /** The tab's client. Fixed by context: there is no client select. */
  client: Client;
  /** null for a create. */
  engagement: Engagement | null;
  onClose: () => void;
}) {
  const editing = engagement !== null;
  const create = useAddEngagement();
  const update = useUpdateEngagement();
  const catalog = useLookupCatalog();
  const countries = useCountries();

  const [code, setCode] = useState(engagement?.code ?? '');
  const [name, setName] = useState(engagement?.name ?? '');
  const [startDate, setStartDate] = useState(engagement?.start_date ?? '');
  const [endDate, setEndDate] = useState(engagement?.end_date ?? '');
  const [plannedEnd, setPlannedEnd] = useState(
    engagement?.planned_end_date ?? '',
  );
  const [statusId, setStatusId] = useState('');
  const [stageId, setStageId] = useState(engagement?.delivery_stage?.id ?? '');
  const [countryId, setCountryId] = useState(
    engagement?.delivery_country?.id ?? '',
  );
  const [productType, setProductType] = useState(engagement?.product_type ?? '');
  const [services, setServices] = useState(engagement?.services ?? '');
  const [methodology, setMethodology] = useState(engagement?.methodology ?? '');
  const [pal, setPal] = useState(engagement?.pal ?? '');
  const [riskLevel, setRiskLevel] = useState(engagement?.risk_level ?? '');
  const [pqrRating, setPqrRating] = useState(engagement?.pqr_rating ?? '');
  const [pqrResult, setPqrResult] = useState(
    engagement?.pqr_result == null ? '' : String(engagement.pqr_result),
  );
  const [portalName, setPortalName] = useState(engagement?.portal_name ?? '');
  const [portalUrl, setPortalUrl] = useState(engagement?.portal_url ?? '');
  const [dashboardUrl, setDashboardUrl] = useState(
    engagement?.dashboard_url ?? '',
  );
  const [program, setProgram] = useState(engagement?.delivery_program ?? '');

  const statuses = useMemo(
    () =>
      (
        catalog.data?.types.find((type) => type.code === ENTITY_STATUS_TYPE)
          ?.values ?? []
      ).filter(
        /*
         * Only active values are offered for a NEW record; the table always
         * renders the STORED value, active or not. f2.2's consumer rule,
         * applied without restatement.
         */
        (value) => value.is_active,
      ),
    [catalog.data],
  );
  const stages = useMemo(
    () =>
      (
        catalog.data?.types.find((type) => type.code === DELIVERY_STAGE_TYPE)
          ?.values ?? []
      ).filter((value) => value.is_active),
    [catalog.data],
  );

  const failure = create.error ?? update.error;
  const busy = create.isPending || update.isPending;

  function submit() {
    if (editing) {
      /*
       * Every field is sent, so clearing one is expressible: the server
       * distinguishes a sent `null` from an absent field, and clearing a
       * secondary attribute is a real instruction. `code`, `client_id` and
       * `status_id` are absent from this shape because the server refuses all
       * three on a PATCH -- the first two are immutable, and status moves
       * through the row's own transition actions.
       */
      const change: EngagementUpdate = {
        name,
        start_date: startDate,
        end_date: endDate,
        planned_end_date: orNull(plannedEnd),
        delivery_stage_id: stageId || null,
        delivery_country_id: countryId || null,
        product_type: orNull(productType),
        services: orNull(services),
        methodology: orNull(methodology),
        pal: orNull(pal),
        risk_level: orNull(riskLevel),
        pqr_rating: orNull(pqrRating),
        pqr_result: orNull(pqrResult),
        portal_name: orNull(portalName),
        portal_url: orNull(portalUrl),
        dashboard_url: orNull(dashboardUrl),
        delivery_program: orNull(program),
      };
      update.mutate(
        { engagementId: engagement.id, change },
        { onSuccess: onClose },
      );
      return;
    }

    create.mutate(
      {
        client_id: client.id,
        code,
        name,
        start_date: startDate,
        end_date: endDate,
        /*
         * Omitted rather than guessed: the server defaults to ACTIVE, and no
         * status literal exists anywhere in this application.
         */
        ...(statusId ? { status_id: statusId } : {}),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title={editing ? 'Edit engagement' : 'New engagement'}
      subtitle={`Under ${client.name}`}
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      submitLabel={editing ? 'Save engagement' : 'Create engagement'}
      submitIcon={<Check aria-hidden="true" />}
      wide={editing}
      layout="plain"
      /*
       * Shown only when the server refused without naming a field, so a
       * field-level problem is never duplicated at the top of the dialog.
       */
      message={generalMessage(failure, ...FIELD_KEYS)}
    >
      <fieldset className={editing ? 'mb-4' : undefined}>
        <div className="fieldset-title">Identity</div>
        <div className="grid grid-3">
          <DialogField
            label="KE code"
            htmlFor="eng-code"
            problem={problemFor(failure, 'code')}
            hint={
              editing
                ? 'Immutable. Kimble issues it, and every feed joins on it'
                : 'The Kimble engagement code: KE and exactly six digits, e.g. KE051816. Unique across every client'
            }
          >
            <input
              id="eng-code"
              type="text"
              value={code}
              readOnly={editing}
              required
              placeholder="KE051816"
              onChange={(event) => setCode(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="Engagement name"
            htmlFor="eng-name"
            span
            problem={problemFor(failure, 'name')}
            hint="Unique across every client, because the master workbook's hierarchy sheet carries no code and resolves an engagement by name alone"
          >
            <input
              id="eng-name"
              type="text"
              value={name}
              required
              placeholder="BX2026 Data, Cloud & Developer Experience"
              onChange={(event) => setName(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="Start date"
            htmlFor="eng-start"
            problem={problemFor(failure, 'start_date')}
            hint="Required. Every one of the 70 engagements in the master has one"
          >
            <input
              id="eng-start"
              type="date"
              value={startDate}
              required
              onChange={(event) => setStartDate(event.target.value)}
            />
          </DialogField>

          <DialogField
            label="End date"
            htmlFor="eng-end"
            problem={problemFor(failure, 'end_date')}
            hint="Required, and never before the start. A one-day engagement is allowed"
          >
            <input
              id="eng-end"
              type="date"
              value={endDate}
              required
              onChange={(event) => setEndDate(event.target.value)}
            />
          </DialogField>

          {editing && (
            <DialogField
              label="Planned end date"
              htmlFor="eng-planned"
              problem={problemFor(failure, 'planned_end_date')}
              hint="Optional, and rarely used: 1 of the 70 legacy engagements records one"
            >
              <input
                id="eng-planned"
                type="date"
                value={plannedEnd}
                onChange={(event) => setPlannedEnd(event.target.value)}
              />
            </DialogField>
          )}
        </div>
      </fieldset>

      {/*
        The secondary attributes, on EDIT only. The brief: "they belong on a
        details panel, not in the primary create form."
      */}
      {editing && (
        <>
          <fieldset className="mb-4">
            <div className="fieldset-title">Commercial</div>
            <div className="grid grid-3">
              <DialogField
                label="Delivery stage"
                htmlFor="eng-stage"
                problem={problemFor(failure, 'delivery_stage_id')}
                hint="Independent of the status: a Final Close engagement can still be Active while its last invoice settles"
              >
                <select
                  id="eng-stage"
                  value={stageId}
                  disabled={catalog.isPending}
                  onChange={(event) => setStageId(event.target.value)}
                >
                  <option value="">Not recorded</option>
                  {stages.map((value) => (
                    <option key={value.id} value={value.id}>
                      {value.name}
                    </option>
                  ))}
                </select>
              </DialogField>

              <DialogField
                label="Product type"
                htmlFor="eng-product"
                problem={problemFor(failure, 'product_type')}
                hint="As the source writes it, e.g. Time & Materials"
              >
                <input
                  id="eng-product"
                  type="text"
                  value={productType}
                  onChange={(event) => setProductType(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="Services"
                htmlFor="eng-services"
                problem={problemFor(failure, 'services')}
              >
                <input
                  id="eng-services"
                  type="text"
                  value={services}
                  onChange={(event) => setServices(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="Methodology"
                htmlFor="eng-method"
                problem={problemFor(failure, 'methodology')}
              >
                <input
                  id="eng-method"
                  type="text"
                  value={methodology}
                  onChange={(event) => setMethodology(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="Delivery program"
                htmlFor="eng-program"
                problem={problemFor(failure, 'delivery_program')}
              >
                <input
                  id="eng-program"
                  type="text"
                  value={program}
                  onChange={(event) => setProgram(event.target.value)}
                />
              </DialogField>
            </div>
          </fieldset>

          <fieldset className="mb-4">
            <div className="fieldset-title">Delivery &amp; quality</div>
            <div className="grid grid-3">
              <DialogField
                label="Delivery country"
                htmlFor="eng-country"
                problem={problemFor(failure, 'delivery_country_id')}
                hint="The seeded reference list. The legacy writes USA, not United States"
              >
                <select
                  id="eng-country"
                  value={countryId}
                  disabled={countries.isPending}
                  onChange={(event) => setCountryId(event.target.value)}
                >
                  <option value="">Not recorded</option>
                  {(countries.data?.countries ?? [])
                    .filter((country) => country.is_active)
                    .map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                </select>
              </DialogField>

              <DialogField
                label="PAL tier"
                htmlFor="eng-pal"
                problem={problemFor(failure, 'pal')}
                hint="Measured in the legacy: Bronze 60, Gold 8, Silver 2"
              >
                <input
                  id="eng-pal"
                  type="text"
                  value={pal}
                  onChange={(event) => setPal(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="Risk level"
                htmlFor="eng-risk"
                problem={problemFor(failure, 'risk_level')}
              >
                <input
                  id="eng-risk"
                  type="text"
                  value={riskLevel}
                  onChange={(event) => setRiskLevel(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="PQR rating"
                htmlFor="eng-pqr-rating"
                problem={problemFor(failure, 'pqr_rating')}
                hint="The legacy writes a single letter, G — not Green"
              >
                <input
                  id="eng-pqr-rating"
                  type="text"
                  value={pqrRating}
                  onChange={(event) => setPqrRating(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="PQR result"
                htmlFor="eng-pqr-result"
                problem={problemFor(failure, 'pqr_result')}
                hint="A rating, not an amount. The measured values run 1.31 to 2.00"
              >
                <input
                  id="eng-pqr-result"
                  type="number"
                  step="0.01"
                  min="0"
                  value={pqrResult}
                  onChange={(event) => setPqrResult(event.target.value)}
                />
              </DialogField>
            </div>
          </fieldset>

          <fieldset>
            <div className="fieldset-title">Links</div>
            <div className="grid grid-3">
              <DialogField
                label="Portal name"
                htmlFor="eng-portal-name"
                problem={problemFor(failure, 'portal_name')}
              >
                <input
                  id="eng-portal-name"
                  type="text"
                  value={portalName}
                  onChange={(event) => setPortalName(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="Portal URL"
                htmlFor="eng-portal-url"
                problem={problemFor(failure, 'portal_url')}
              >
                <input
                  id="eng-portal-url"
                  type="url"
                  value={portalUrl}
                  onChange={(event) => setPortalUrl(event.target.value)}
                />
              </DialogField>

              <DialogField
                label="Dashboard URL"
                htmlFor="eng-dashboard-url"
                problem={problemFor(failure, 'dashboard_url')}
              >
                <input
                  id="eng-dashboard-url"
                  type="url"
                  value={dashboardUrl}
                  onChange={(event) => setDashboardUrl(event.target.value)}
                />
              </DialogField>
            </div>
          </fieldset>
        </>
      )}

      {!editing && (
        <fieldset>
          <div className="fieldset-title">Status</div>
          <div className="grid grid-2">
            <DialogField
              label="Status"
              htmlFor="eng-status"
              problem={problemFor(failure, 'status_id')}
              hint="Engagements are never deleted — deactivation is the only removal"
            >
              <select
                id="eng-status"
                value={statusId}
                disabled={catalog.isPending}
                onChange={(event) => setStatusId(event.target.value)}
              >
                {/*
                  The empty option is the DEFAULT, not "none": the server
                  assigns ACTIVE, so the dialog does not have to know its id.
                */}
                <option value="">Active (the default)</option>
                {statuses.map((value) => (
                  <option key={value.id} value={value.id}>
                    {value.name}
                  </option>
                ))}
              </select>
            </DialogField>
          </div>
          <p className="hint">
            Delivery elements are discovered from the Kimble feed, not entered
            here. The Project Head is named on the Ownership card, where an
            assignment keeps its own history.
          </p>
        </fieldset>
      )}
    </Modal>
  );
}
